/**
 * WebSocket Client — KollegePlus Messenger
 *
 * Manages the WebSocket connection for real-time features:
 * - Messages, typing indicators, presence, read receipts
 * - Auto-reconnect with exponential backoff
 * - Event-based API
 */

import { getAccessToken, ensureValidToken } from "./api";

// ============================================================
// Types (mirrors server types)
// ============================================================

export type ServerMessageType =
  | "connected"
  | "message"
  | "message_edit"
  | "message_delete"
  | "typing"
  | "typing_stop"
  | "presence"
  | "read_receipt"
  | "channel_update"
  | "channel_added"
  | "reaction"
  | "bot_stream_start"
  | "bot_stream_chunk"
  | "bot_stream_end"
  | "e2ee_session_reset"
  | "device_deactivated"
  | "dm_recover_request"
  | "dm_recover_replaced"
  | "prekey-pool-low"
  | "proxy_backup_key_request"
  | "pong"
  | "error";

export interface ServerMessage {
  type: ServerMessageType;
  [key: string]: unknown;
}

export type ClientMessageType =
  | "send_message"
  | "edit_message"
  | "delete_message"
  | "typing"
  | "read_receipt"
  | "subscribe"
  | "unsubscribe"
  | "ping"
  | "reaction"
  | "voice_mode"
  | "session_reset"
  | "request_sender_key"
  | "recovery_key_request"
  | "dm_recover_request"
  | "request_channel_redistribute"
  | "backup_key_request";

export interface ClientMessage {
  type: ClientMessageType;
  [key: string]: unknown;
}

// ============================================================
// Event Emitter
// ============================================================

type EventHandler = (data: ServerMessage) => void;

class EventBus {
  private handlers = new Map<string, Set<EventHandler>>();

  on(event: string, handler: EventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
    return () => this.handlers.get(event)?.delete(handler);
  }

  emit(event: string, data: ServerMessage): void {
    this.handlers.get(event)?.forEach((handler) => handler(data));
    this.handlers.get("*")?.forEach((handler) => handler(data));
  }
}

// ============================================================
// WebSocket Manager
// ============================================================

class WebSocketManager {
  private ws: WebSocket | null = null;
  private events = new EventBus();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private isIntentionallyClosed = false;
  private isConnecting = false;
  private visibilityBound = false;

  /** Observable connection state: 'connected' | 'disconnected' | 'reconnecting' */
  connectionState: "connected" | "disconnected" | "reconnecting" = "disconnected";

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Connect to the WebSocket server.
   * Refreshes the access token if expired before connecting.
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.isConnecting) return; // Prevent concurrent connect attempts

    this.isConnecting = true;
    this.setupVisibilityHandler();

    try {
      // Ensure we have a valid (non-expired) token before connecting
      let token: string | null;
      try {
        token = await ensureValidToken();
      } catch {
        token = getAccessToken(); // fallback to current token
      }

      if (!token) {
        console.warn("[WS] No access token — cannot connect");
        // Still schedule reconnect so we retry when token becomes available
        if (!this.isIntentionallyClosed) {
          this.connectionState = "reconnecting";
          this.scheduleReconnect();
        }
        return;
      }

      this.isIntentionallyClosed = false;
      const isTauri = !!(window as unknown as Record<string, unknown>).__TAURI_INTERNALS__;
      const wsHost = isTauri ? "app.kollegeplus.de" : window.location.host;
      const protocol = isTauri ? "wss:" : (window.location.protocol === "https:" ? "wss:" : "ws:");
      // Include device ID for multi-device E2EE routing
      const deviceId = localStorage.getItem("kollegeplus:deviceId");
      const deviceParam = deviceId ? `&deviceId=${encodeURIComponent(deviceId)}` : "";
      const wsUrl = `${protocol}//${wsHost}/ws?token=${encodeURIComponent(token)}${deviceParam}`;

      // Close stale socket if lingering
      if (this.ws) {
        try { this.ws.close(); } catch { /* ignore */ }
        this.ws = null;
      }

      this.ws = new WebSocket(wsUrl);

      // Safety timeout: if the socket doesn't open within 10s, close & retry
      const connectTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          console.warn("[WS] Connect timeout — forcing close");
          try { this.ws.close(); } catch { /* ignore */ }
        }
      }, 10_000);

      this.ws.onopen = () => {
        clearTimeout(connectTimeout);
        console.debug("[WS] Connected");
        this.reconnectAttempts = 0;
        this.connectionState = "connected";
        this.startPing();
        this.events.emit("_connected", { type: "connected" });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as ServerMessage;
          this.events.emit(data.type, data);
        } catch (err) {
          console.error("[WS] Failed to parse message:", err);
        }
      };

      this.ws.onclose = (event) => {
        clearTimeout(connectTimeout);
        console.debug(`[WS] Closed (code=${event.code}, reason=${event.reason})`);
        this.stopPing();
        this.ws = null;

        if (this.isIntentionallyClosed) {
          this.connectionState = "disconnected";
        } else {
          this.connectionState = "reconnecting";
          this.events.emit("_disconnected", { type: "error", code: "disconnected", message: event.reason });

          // If closed with 4001 (auth error), force token refresh before reconnect
          if (event.code === 4001) {
            console.debug("[WS] Auth error — refreshing token before reconnect");
            ensureValidToken().catch(() => {});
          }
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (err) => {
        console.error("[WS] Error:", err);
        // onclose will fire after onerror — no need to schedule reconnect here
      };
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.connectionState = "disconnected";
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close(1000, "Client disconnect");
    this.ws = null;
  }

  /**
   * Send a message to the server
   */
  send(message: ClientMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("[WS] Not connected — message dropped:", message.type);
      return;
    }
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Subscribe to server events
   */
  on(event: string, handler: EventHandler): () => void {
    return this.events.on(event, handler);
  }

  // ── Convenience Methods ──

  sendMessage(channelId: string, ciphertext: string, contentType = "text"): void {
    this.send({
      type: "send_message",
      channelId,
      ciphertext,
      contentType,
    });
  }

  sendTyping(channelId: string, isTyping: boolean): void {
    this.send({
      type: "typing",
      channelId,
      isTyping,
    });
  }

  sendReadReceipt(channelId: string, messageId: string): void {
    this.send({
      type: "read_receipt",
      channelId,
      messageId,
    });
  }

  subscribeChannel(channelId: string): void {
    this.send({ type: "subscribe", channelId });
  }

  unsubscribeChannel(channelId: string): void {
    this.send({ type: "unsubscribe", channelId });
  }

  // ── Internal ──

  private pongTimeout: ReturnType<typeof setTimeout> | null = null;

  private startPing(): void {
    // Listen for pong responses to detect dead connections
    this.events.on("pong", () => {
      if (this.pongTimeout) {
        clearTimeout(this.pongTimeout);
        this.pongTimeout = null;
      }
    });

    this.pingInterval = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      this.send({ type: "ping" });

      // If no pong within 5s, connection is likely dead — force reconnect
      this.pongTimeout = setTimeout(() => {
        if (this.ws && !this.isIntentionallyClosed) {
          console.warn("[WS] No pong received within 5s — forcing reconnect");
          try { this.ws.close(4000, "Pong timeout"); } catch { /* ignore */ }
        }
      }, 5_000);
    }, 30_000); // Every 30s
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private scheduleReconnect(): void {
    // Clear any existing timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.connectionState = "reconnecting";

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s cap + jitter (±25%)
    const baseDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30_000);
    const jitter = baseDelay * (0.75 + Math.random() * 0.5); // 75%-125% of base
    const delay = Math.round(jitter);
    this.reconnectAttempts++;

    console.debug(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => {
      void this.connect();
    }, delay);
  }

  /**
   * Handle visibility changes (mobile browser tab switch, screen lock/unlock).
   * iOS Safari kills WebSocket connections when the page is backgrounded;
   * reconnect immediately when the page becomes visible again.
   */
  private setupVisibilityHandler(): void {
    if (this.visibilityBound) return;
    this.visibilityBound = true;

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && !this.isIntentionallyClosed) {
        // Page became visible — check if WS is still alive
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          console.debug("[WS] Page visible — reconnecting");
          this.reconnectAttempts = 0; // Reset backoff for immediate reconnect
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          void this.connect();
        }
      }
    });

    // Also handle online/offline events
    window.addEventListener("online", () => {
      if (!this.isIntentionallyClosed && (!this.ws || this.ws.readyState !== WebSocket.OPEN)) {
        console.debug("[WS] Network online — reconnecting");
        this.reconnectAttempts = 0;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        void this.connect();
      }
    });
  }
}

// Singleton instance
export const ws = new WebSocketManager();
